import React, { useMemo } from 'react';
import {
  Field,
  FieldLabel,
  FieldDescription,
  FieldError,
  Input,
  Switch,
  Alert,
  AlertDescription,
  AlertTitle,
} from '../ui';
import { Control, Controller, UseFormRegister, UseFormWatch, FieldPath } from 'react-hook-form';
import { AcademyFormData } from '@court-sync/shared';
import { AcademySettingsSection } from './AcademySettingsSection';
import { SmsCommunicationsSection } from './SmsCommunicationsSection';
import { Info } from 'lucide-react';

interface LessonSettingsTabProps {
  register: UseFormRegister<AcademyFormData>;
  control: Control<AcademyFormData>;
  watch: UseFormWatch<AcademyFormData>;
  errors: Record<string, any>;
}

function SettingsSwitchField({
  id,
  name,
  control,
  title,
  description,
}: {
  id: string;
  name: FieldPath<AcademyFormData>;
  control: Control<AcademyFormData>;
  title: string;
  description?: string;
}) {
  return (
    <div className="flex items-start gap-3">
      <Controller
        name={name}
        control={control}
        render={({ field }) => (
          <Switch id={id} checked={!!field.value} onCheckedChange={field.onChange} />
        )}
      />
      <label htmlFor={id} className="cursor-pointer text-sm text-gray-700 dark:text-gray-300">
        <span className="font-medium text-gray-900 dark:text-white">{title}</span>
        {description ? (
          <p className="mt-1 text-gray-600 dark:text-gray-400">{description}</p>
        ) : null}
      </label>
    </div>
  );
}

function SettingsSectionBody({ children }: { children: React.ReactNode }) {
  return <div className="space-y-8">{children}</div>;
}

function SettingsInsetPanel({ children }: { children: React.ReactNode }) {
  return (
    <div className="space-y-5 rounded-lg border border-border bg-muted/20 p-4 sm:p-5">
      {children}
    </div>
  );
}

export const LessonSettingsTab: React.FC<LessonSettingsTabProps> = ({
  register,
  control,
  watch,
  errors,
}) => {
  const makeupLessonEnabled = watch('makeupLessonEnabled');
  const makeupCreditExpiryMode = watch('makeupCreditExpiryMode') ?? 'days';
  const makeupCreditMaxPerPeriod = watch('makeupCreditMaxPerPeriod');
  const maxCapEnabled =
    makeupCreditMaxPerPeriod != null && Number(makeupCreditMaxPerPeriod) >= 1;
  const lowEnrollmentAlertsEnabled = watch('lowEnrollmentAlertsEnabled');
  const minimumStudentsForLesson = watch('minimumStudentsForLesson');
  const notificationDeadline = useMemo(() => watch('makeupNotificationDeadlineHours') || 24, [watch]);
  const creditExpiry = useMemo(() => watch('makeupCreditExpiryDays') || 30, [watch]);

  return (
    <div>
      <AcademySettingsSection
        title="Low enrolment alerts"
        description="Set a minimum number of students for a lesson to run. When bookings fall below that number, alerts appear on the dashboard so you can cancel or postpone the session."
        divider="none"
      >
        <SettingsSectionBody>
          <SettingsSwitchField
            id="low-enrollment-enabled"
            name="lowEnrollmentAlertsEnabled"
            control={control}
            title="Enable low enrolment alerts"
            description="Turn off to hide dashboard alerts while keeping your threshold saved for later."
          />

          {lowEnrollmentAlertsEnabled ? (
            <SettingsInsetPanel>
              <Field>
                <FieldLabel>Minimum students required</FieldLabel>
                <FieldDescription>
                  Lessons with fewer than this many bookings are treated as low enrolment.
                </FieldDescription>
                <Controller
                  name="minimumStudentsForLesson"
                  control={control}
                  render={({ field }) => (
                    <Input
                      type="number"
                      min={1}
                      max={50}
                      placeholder="e.g., 3"
                      className="max-w-[8rem]"
                      value={field.value ?? ''}
                      onChange={(e) => {
                        const val = e.target.value;
                        if (val === '') {
                          field.onChange(undefined);
                        } else {
                          const num = parseInt(val, 10);
                          field.onChange(Number.isNaN(num) ? undefined : num);
                        }
                      }}
                    />
                  )}
                />
                {errors.minimumStudentsForLesson ? (
                  <FieldError>{(errors.minimumStudentsForLesson as any).message}</FieldError>
                ) : null}
              </Field>

              {minimumStudentsForLesson ? (
                <Alert>
                  <Info className="h-4 w-4" aria-hidden />
                  <AlertDescription>
                    Lessons with fewer than {minimumStudentsForLesson} students will appear in the
                    Low Enrollment Alert section on your dashboard.
                  </AlertDescription>
                </Alert>
              ) : null}
            </SettingsInsetPanel>
          ) : null}
        </SettingsSectionBody>
      </AcademySettingsSection>

      <AcademySettingsSection
        title="Make-up lesson credits"
        description="Let students report absences and earn credits they can use to book a future lesson."
      >
        <SettingsSectionBody>
          <SettingsSwitchField
            id="makeup-enabled"
            name="makeupLessonEnabled"
            control={control}
            title="Enable make-up lesson credits"
            description="Students report absences within your deadline and earn credits redeemable on compatible lessons."
          />

          {makeupLessonEnabled ? (
            <SettingsInsetPanel>
              <Field>
                <FieldLabel>Absence notification deadline</FieldLabel>
                <FieldDescription>
                  How many hours before a lesson must a student notify their absence to be eligible
                  for a make-up credit?
                </FieldDescription>
                <div className="flex items-center gap-2">
                  <Input
                    type="number"
                    min="1"
                    max="240"
                    placeholder="24"
                    {...register('makeupNotificationDeadlineHours', {
                      valueAsNumber: true,
                    })}
                    className="w-20"
                  />
                  <span className="text-sm text-gray-600 dark:text-gray-400">hours before lesson</span>
                </div>
                {errors.makeupNotificationDeadlineHours ? (
                  <FieldError>
                    {(errors.makeupNotificationDeadlineHours as any).message}
                  </FieldError>
                ) : null}
              </Field>

              <Field>
                <FieldLabel>Credit expiration</FieldLabel>
                <FieldDescription>
                  Choose whether credits expire after a fixed number of days, or at the end of the
                  term the student is enrolled in (group lessons with a term).
                </FieldDescription>
                <div className="space-y-3" role="radiogroup" aria-label="Make-up credit expiration">
                  <Controller
                    name="makeupCreditExpiryMode"
                    control={control}
                    render={({ field }) => (
                      <>
                        <label className="flex cursor-pointer items-start gap-3">
                          <input
                            type="radio"
                            className="mt-1"
                            checked={field.value === 'days' || field.value === undefined}
                            onChange={() => field.onChange('days')}
                          />
                          <span className="text-sm text-gray-700 dark:text-gray-300">
                            <span className="font-medium">Fixed number of days</span>
                            <p className="mt-0.5 text-gray-600 dark:text-gray-400">
                              Credit expires a set number of days after it is earned.
                            </p>
                          </span>
                        </label>
                        <label className="flex cursor-pointer items-start gap-3">
                          <input
                            type="radio"
                            className="mt-1"
                            checked={field.value === 'end_of_term'}
                            onChange={() => field.onChange('end_of_term')}
                          />
                          <span className="text-sm text-gray-700 dark:text-gray-300">
                            <span className="font-medium">End of enrolled term</span>
                            <p className="mt-0.5 text-gray-600 dark:text-gray-400">
                              Credit expires at the end of the term for that booking (with a minimum
                              grace period).
                            </p>
                          </span>
                        </label>
                      </>
                    )}
                  />
                </div>
              </Field>

              {makeupCreditExpiryMode === 'days' ? (
                <Field>
                  <FieldLabel>Credit expiration period</FieldLabel>
                  <FieldDescription>
                    How many days after earning a credit can a student use it?
                  </FieldDescription>
                  <div className="flex items-center gap-2">
                    <Input
                      type="number"
                      min="7"
                      max="365"
                      placeholder="30"
                      {...register('makeupCreditExpiryDays', {
                        valueAsNumber: true,
                      })}
                      className="w-20"
                    />
                    <span className="text-sm text-gray-600 dark:text-gray-400">days from earning</span>
                  </div>
                  {errors.makeupCreditExpiryDays ? (
                    <FieldError>{(errors.makeupCreditExpiryDays as any).message}</FieldError>
                  ) : null}
                </Field>
              ) : null}

              {makeupCreditExpiryMode === 'end_of_term' ? (
                <Alert variant="warning">
                  <AlertTitle>Please note</AlertTitle>
                  <AlertDescription>
                    <ul className="list-inside list-disc space-y-1">
                      <li>
                        Credits from <strong>private or camp lessons</strong> (no term) still use the
                        default {creditExpiry}-day window—there is no term end date to apply.
                      </li>
                      <li>
                        If a credit is earned near or after the term end (e.g. last-day cancellation),
                        a <strong>minimum 7-day grace period</strong> applies so the credit is not
                        immediately expired.
                      </li>
                    </ul>
                  </AlertDescription>
                </Alert>
              ) : null}

              <Field>
                <FieldLabel>Maximum credits earned per period (optional)</FieldLabel>
                <FieldDescription>
                  Limit how many make-up credits a student can earn in a rolling window or per enrolled
                  term. Leave empty for no limit.
                </FieldDescription>
                <Controller
                  name="makeupCreditMaxPerPeriod"
                  control={control}
                  render={({ field }) => (
                    <Input
                      type="number"
                      min={1}
                      max={99}
                      placeholder="No limit"
                      value={field.value ?? ''}
                      onChange={(e) => {
                        const v = e.target.value;
                        if (v === '') {
                          field.onChange(undefined);
                        } else {
                          const n = parseInt(v, 10);
                          field.onChange(Number.isNaN(n) ? undefined : n);
                        }
                      }}
                      className="w-24"
                    />
                  )}
                />
                {errors.makeupCreditMaxPerPeriod ? (
                  <FieldError>
                    {(errors.makeupCreditMaxPerPeriod as { message?: string }).message}
                  </FieldError>
                ) : null}
              </Field>

              {maxCapEnabled ? (
                <>
                  <Field>
                    <FieldLabel>Measure the limit by</FieldLabel>
                    <FieldDescription>
                      Rolling days counts credits earned in the last N days. Per enrolled term counts
                      credits tied to the same term as the booking (private/camp without a term uses the
                      rolling window below).
                    </FieldDescription>
                    <div
                      className="space-y-3"
                      role="radiogroup"
                      aria-label="Make-up credit earn limit period"
                    >
                      <Controller
                        name="makeupCreditMaxPeriodMode"
                        control={control}
                        render={({ field }) => (
                          <>
                            <label className="flex cursor-pointer items-start gap-3">
                              <input
                                type="radio"
                                className="mt-1"
                                checked={field.value === 'days' || field.value === undefined}
                                onChange={() => field.onChange('days')}
                              />
                              <span className="text-sm text-gray-700 dark:text-gray-300">
                                <span className="font-medium">Rolling days</span>
                                <p className="mt-0.5 text-gray-600 dark:text-gray-400">
                                  Cap applies to credits earned in the last N days.
                                </p>
                              </span>
                            </label>
                            <label className="flex cursor-pointer items-start gap-3">
                              <input
                                type="radio"
                                className="mt-1"
                                checked={field.value === 'enrolled_term'}
                                onChange={() => field.onChange('enrolled_term')}
                              />
                              <span className="text-sm text-gray-700 dark:text-gray-300">
                                <span className="font-medium">Per enrolled term</span>
                                <p className="mt-0.5 text-gray-600 dark:text-gray-400">
                                  Cap applies per term (same term as the booking).
                                </p>
                              </span>
                            </label>
                          </>
                        )}
                      />
                    </div>
                    {errors.makeupCreditMaxPeriodMode ? (
                      <FieldError>
                        {(errors.makeupCreditMaxPeriodMode as { message?: string }).message}
                      </FieldError>
                    ) : null}
                  </Field>

                  <Field>
                    <FieldLabel>Rolling window (days)</FieldLabel>
                    <FieldDescription>
                      Used for rolling-days mode, and when a booking has no term while using per-term
                      mode.
                    </FieldDescription>
                    <div className="flex items-center gap-2">
                      <Input
                        type="number"
                        min={1}
                        max={365}
                        placeholder="30"
                        {...register('makeupCreditMaxPeriodDays', {
                          valueAsNumber: true,
                        })}
                        className="w-20"
                      />
                      <span className="text-sm text-gray-600 dark:text-gray-400">days</span>
                    </div>
                    {errors.makeupCreditMaxPeriodDays ? (
                      <FieldError>
                        {(errors.makeupCreditMaxPeriodDays as { message?: string }).message}
                      </FieldError>
                    ) : null}
                  </Field>
                </>
              ) : null}

              <Alert>
                <Info className="h-4 w-4" aria-hidden />
                <AlertTitle>How make-up credits work</AlertTitle>
                <AlertDescription>
                  <ul className="space-y-1">
                    <li>
                      Student misses a lesson and reports absence within {notificationDeadline} hours
                    </li>
                    <li>Earns 1 make-up credit for that lesson</li>
                    <li>
                      Can use the credit to book any lesson of the same type and skill level
                    </li>
                    {makeupCreditExpiryMode === 'end_of_term' ? (
                      <li>
                        For term-based group lessons, credits expire at the end of that term (with
                        grace rules above); otherwise the {creditExpiry}-day default applies
                      </li>
                    ) : (
                      <li>Credit expires in {creditExpiry} days if not used</li>
                    )}
                  </ul>
                </AlertDescription>
              </Alert>
            </SettingsInsetPanel>
          ) : null}
        </SettingsSectionBody>
      </AcademySettingsSection>

      <AcademySettingsSection
        title="Waitlist for full lessons"
        description="Control what happens when a lesson reaches capacity."
      >
        <SettingsSectionBody>
          <SettingsSwitchField
            id="allow-enrolment-when-full"
            name="allowEnrolmentWhenFull"
            control={control}
            title="Allow enrolment when lesson is full"
            description="When enabled, students can continue booking even when the lesson has reached capacity. Waitlist will not be used for over-capacity scenarios."
          />

          <SettingsSwitchField
            id="waitlist-enabled"
            name="waitlistEnabled"
            control={control}
            title="Enable waitlist for full lessons"
            description="When enabled, guardians can join a waitlist when a lesson is fully booked. When a spot opens, the first person is notified."
          />

          {watch('waitlistEnabled') ? (
            <SettingsInsetPanel>
              <Field>
                <FieldLabel>Hours to claim spot</FieldLabel>
                <FieldDescription>
                  How many hours does the first person on the waitlist have to claim a spot when it
                  opens?
                </FieldDescription>
                <div className="flex items-center gap-2">
                  <Input
                    type="number"
                    min={1}
                    max={168}
                    placeholder="24"
                    {...register('waitlistExpiryHours', {
                      valueAsNumber: true,
                    })}
                    className="w-20"
                  />
                  <span className="text-sm text-gray-600 dark:text-gray-400">hours</span>
                </div>
                {errors.waitlistExpiryHours ? (
                  <FieldError>{(errors.waitlistExpiryHours as any).message}</FieldError>
                ) : null}
              </Field>
            </SettingsInsetPanel>
          ) : null}
        </SettingsSectionBody>
      </AcademySettingsSection>

      <AcademySettingsSection
        title="Private lesson booking (public)"
        description="Let students book private lessons from your public academy page using coach availability and hourly rates. Coaches must have an hourly rate set."
      >
        <SettingsSectionBody>
          <SettingsSwitchField
            id="private-booking-enabled"
            name="privateBookingEnabled"
            control={control}
            title="Enable student-initiated private bookings"
          />

          <SettingsInsetPanel>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
              <Field>
                <FieldLabel>Min duration (minutes)</FieldLabel>
                <Input
                  type="number"
                  min={15}
                  max={480}
                  {...register('privateBookingMinDurationMinutes', { valueAsNumber: true })}
                />
              </Field>
              <Field>
                <FieldLabel>Max duration (minutes)</FieldLabel>
                <Input
                  type="number"
                  min={15}
                  max={480}
                  {...register('privateBookingMaxDurationMinutes', { valueAsNumber: true })}
                />
              </Field>
              <Field>
                <FieldLabel>Step (minutes)</FieldLabel>
                <Input
                  type="number"
                  min={5}
                  max={120}
                  {...register('privateBookingDurationStepMinutes', { valueAsNumber: true })}
                />
              </Field>
              <Field className="sm:col-span-3">
                <FieldLabel>Minimum lead time (hours)</FieldLabel>
                <Input
                  type="number"
                  min={0}
                  max={720}
                  placeholder="24"
                  className="max-w-[8rem]"
                  {...register('privateBookingMinLeadHours', { valueAsNumber: true })}
                />
                <FieldDescription>
                  Students cannot book a session that starts sooner than this many hours from when
                  they book (default 24).
                </FieldDescription>
              </Field>
            </div>

            <div className="flex items-start gap-3">
              <Controller
                name="privateBookingApprovalRequired"
                control={control}
                render={({ field }) => (
                  <Switch
                    id="private-booking-approval"
                    checked={field.value !== false}
                    onCheckedChange={field.onChange}
                  />
                )}
              />
              <label
                htmlFor="private-booking-approval"
                className="cursor-pointer text-sm text-gray-700 dark:text-gray-300"
              >
                <span className="font-medium text-gray-900 dark:text-white">
                  Require owner approval before payment
                </span>
                <p className="mt-1 text-gray-600 dark:text-gray-400">
                  When off, bookings are created immediately for payment (if rules pass).
                </p>
              </label>
            </div>
          </SettingsInsetPanel>
        </SettingsSectionBody>
      </AcademySettingsSection>

      <AcademySettingsSection
        title="Pro-rata term pricing"
        description="Charge students proportionally when they enrol after a term has started."
      >
        <SettingsSectionBody>
          <SettingsSwitchField
            id="pro-rata-enabled"
            name="proRataEnabled"
            control={control}
            title="Enable pro-rata pricing"
            description="When enabled, students who enrol after a term has started are charged proportionally based on remaining sessions (group lessons priced as a full term)."
          />
        </SettingsSectionBody>
      </AcademySettingsSection>

      <SmsCommunicationsSection control={control} />

      <AcademySettingsSection
        title="Coach attendance"
        description="Choose whether coaches can check themselves in or only owners mark attendance."
      >
        <SettingsSectionBody>
          <SettingsSwitchField
            id="allow-coach-self-attendance"
            name="allowCoachSelfAttendance"
            control={control}
            title="Allow coaches to mark their own attendance"
            description="When enabled, coaches can check in from the coach dashboard—My Attendance, My Schedule, and Today's Roster. When off, owners and administrators mark coach attendance from session details on the Calendar."
          />
        </SettingsSectionBody>
      </AcademySettingsSection>

      <AcademySettingsSection
        title="Lesson cancellation"
        description="When you cancel or postpone a lesson:"
      >
        <ul className="space-y-2 text-sm text-gray-600 dark:text-gray-400">
          <li>Students are automatically notified via email with the reason</li>
          <li>For cancellations: students can request a refund or use the credit</li>
          <li>For postponements: the new date/time is communicated automatically</li>
          <li>All changes are logged and tracked in the lesson history</li>
        </ul>
      </AcademySettingsSection>
    </div>
  );
};

export default LessonSettingsTab;
